/**
 * Alerte toast : fond noir semi-transparent, carte succès (vert + icône done) ou échec (rouge),
 * barre de progression pour la durée.
 * @param {string} message - Message à afficher
 * @param {'success'|'error'|'info'|'warning'} type - Type d'alerte
 * @param {number} duration - Durée en ms avant disparition (défaut 4000)
 */
export function showToast(message, type = 'info', duration = 4000) {
  if (!message || typeof message !== 'string') return;

  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'alert');
  overlay.className = 'fixed inset-0 z-[9999] flex items-center justify-center p-4';
  overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';

  const isSuccess = type === 'success';
  const isError = type === 'error';
  const cardBg = isSuccess ? '#16a34a' : isError ? '#dc2626' : type === 'warning' ? '#d97706' : '#2563eb';
  const iconBg = isSuccess ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.25)';

  const card = document.createElement('div');
  card.className = 'rounded-xl shadow-2xl overflow-hidden min-w-[280px] max-w-md';
  card.style.backgroundColor = cardBg;
  card.style.color = '#fff';

  const content = document.createElement('div');
  content.className = 'flex items-center gap-4 p-5';

  const iconWrap = document.createElement('div');
  iconWrap.className = 'flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center';
  iconWrap.style.backgroundColor = iconBg;

  const icon = document.createElement('span');
  icon.setAttribute('aria-hidden', 'true');
  if (isSuccess) {
    icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
  } else if (isError) {
    icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
  } else {
    icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>';
  }
  iconWrap.appendChild(icon);

  const text = document.createElement('p');
  text.className = 'text-white font-medium text-base flex-1';
  text.textContent = message;

  content.appendChild(iconWrap);
  content.appendChild(text);
  card.appendChild(content);

  const progressTrack = document.createElement('div');
  progressTrack.className = 'h-1 w-full';
  progressTrack.style.backgroundColor = 'rgba(255,255,255,0.3)';

  const progressBar = document.createElement('div');
  progressBar.className = 'h-full';
  progressBar.style.backgroundColor = '#fff';
  progressBar.style.width = '100%';
  progressBar.style.transition = 'none';
  progressTrack.appendChild(progressBar);

  card.appendChild(progressTrack);
  overlay.appendChild(card);

  progressBar.style.transition = `width ${duration}ms linear`;
  progressBar.style.width = '100%';
  requestAnimationFrame(() => {
    progressBar.style.width = '0%';
  });

  const remove = () => {
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.25s ease';
    setTimeout(() => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 250);
  };

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      remove();
    }
  });
  card.addEventListener('click', (e) => e.stopPropagation());

  document.body.appendChild(overlay);
  setTimeout(remove, duration);
}

/** Alerte succès (cercle vert, icône done blanche, barre de progression) */
export function toastSuccess(message, duration = 4000) {
  showToast(message, 'success', duration);
}

/** Alerte erreur (fond rouge, icône blanche, barre de progression) */
export function toastError(message, duration = 6000) {
  showToast(message, 'error', duration);
}

/** Alerte information */
export function toastInfo(message, duration = 4000) {
  showToast(message, 'info', duration);
}

/** Alerte avertissement */
export function toastWarning(message, duration = 4000) {
  showToast(message, 'warning', duration);
}
